import { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  countries,
  getCountryByCode,
  isPriorityCountry,
  type Country,
} from '@/data/countries';

// Import flags from country-flag-icons
import * as Flags from 'country-flag-icons/react/3x2';

interface CountrySelectProps {
  /** Selected country ISO code */
  value: string;
  /** Called when country changes */
  onChange: (code: string) => void;
  /** Display mode: 'dialCode' shows +48, 'name' shows Poland */
  displayMode?: 'dialCode' | 'name';
  /** Disable the selector */
  disabled?: boolean;
  /** Custom class name */
  className?: string;
  /** Show border (for standalone use) */
  showBorder?: boolean;
  /** Border radius style */
  borderRadius?: 'full' | 'left' | 'right' | 'none';
}

export default function CountrySelect({
  value,
  onChange,
  displayMode = 'name',
  disabled = false,
  className = '',
  showBorder = true,
  borderRadius = 'full',
}: CountrySelectProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const selectedCountry = getCountryByCode(value) || countries[0];

  // Filter countries based on search
  const filteredCountries = useMemo(() => {
    if (!search.trim()) {
      return countries;
    }

    const searchLower = search.toLowerCase().trim();
    return countries.filter(
      (country) =>
        country.name.toLowerCase().includes(searchLower) ||
        country.dialCode.includes(searchLower) ||
        country.code.toLowerCase().includes(searchLower)
    );
  }, [search]);

  // Separate priority and other countries for display
  const { priorityList, otherList } = useMemo(() => {
    const priority = filteredCountries.filter((c) => isPriorityCountry(c.code));
    const other = filteredCountries.filter((c) => !isPriorityCountry(c.code));
    return { priorityList: priority, otherList: other };
  }, [filteredCountries]);

  // Reset highlight when filter changes
  useEffect(() => {
    setHighlightedIndex(0);
  }, [search]);

  // Handle click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && listRef.current) {
      const highlighted = listRef.current.querySelector('[data-highlighted="true"]');
      if (highlighted) {
        highlighted.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex, isOpen]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const totalItems = filteredCountries.length;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setHighlightedIndex((prev) => (prev + 1) % totalItems);
        break;
      case 'ArrowUp':
        event.preventDefault();
        setHighlightedIndex((prev) => (prev - 1 + totalItems) % totalItems);
        break;
      case 'Enter':
        event.preventDefault();
        if (filteredCountries[highlightedIndex]) {
          handleSelect(filteredCountries[highlightedIndex]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        setIsOpen(false);
        setSearch('');
        break;
    }
  };

  const handleSelect = (country: Country) => {
    onChange(country.code);
    setIsOpen(false);
    setSearch('');
  };

  const borderRadiusClass = {
    full: 'rounded-xl',
    left: 'rounded-l-xl rounded-r-none',
    right: 'rounded-r-xl rounded-l-none',
    none: 'rounded-none',
  }[borderRadius];

  const borderClass = showBorder ? 'border border-gray-200' : '';

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full flex items-center gap-2 px-3 py-3 bg-white/50 ${borderClass} ${borderRadiusClass} hover:bg-white/70 focus:ring-2 focus:ring-mk-pink-500 focus:outline-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed`}
        aria-label={t('dashboard.form.selectCountry', 'Select country')}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <FlagIcon countryCode={selectedCountry.code} className="w-5 h-4 rounded-sm shadow-sm" />
        <span className="text-sm text-gray-700 flex-1 text-left">
          {displayMode === 'dialCode'
            ? `+${selectedCountry.dialCode}`
            : selectedCountry.name}
        </span>
        <ChevronIcon className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div
          className="absolute left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-200 z-50 min-w-[280px] max-h-80 overflow-hidden"
          role="listbox"
          onKeyDown={handleKeyDown}
        >
          {/* Search input */}
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('dashboard.form.searchCountry', 'Search countries...')}
                className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-mk-pink-500 focus:border-transparent focus:outline-none"
                onKeyDown={handleKeyDown}
              />
            </div>
          </div>

          {/* Country list */}
          <div ref={listRef} className="max-h-60 overflow-y-auto">
            {filteredCountries.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                {t('dashboard.form.noCountryFound', 'No country found')}
              </div>
            ) : (
              <>
                {/* Priority countries */}
                {priorityList.length > 0 && (
                  <>
                    {priorityList.map((country, index) => (
                      <CountryOption
                        key={country.code}
                        country={country}
                        isSelected={value === country.code}
                        isHighlighted={index === highlightedIndex}
                        displayMode={displayMode}
                        onClick={() => handleSelect(country)}
                      />
                    ))}
                    {otherList.length > 0 && (
                      <div className="border-t border-gray-100 my-1" />
                    )}
                  </>
                )}

                {/* Other countries */}
                {otherList.map((country, index) => (
                  <CountryOption
                    key={country.code}
                    country={country}
                    isSelected={value === country.code}
                    isHighlighted={priorityList.length + index === highlightedIndex}
                    displayMode={displayMode}
                    onClick={() => handleSelect(country)}
                  />
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface CountryOptionProps {
  country: Country;
  isSelected: boolean;
  isHighlighted: boolean;
  displayMode: 'dialCode' | 'name';
  onClick: () => void;
}

function CountryOption({
  country,
  isSelected,
  isHighlighted,
  displayMode,
  onClick,
}: CountryOptionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
        isSelected
          ? 'bg-mk-pink-50 text-mk-pink-600'
          : isHighlighted
          ? 'bg-gray-100'
          : 'hover:bg-gray-50'
      }`}
      role="option"
      aria-selected={isSelected}
      data-highlighted={isHighlighted}
    >
      <FlagIcon countryCode={country.code} className="w-5 h-4 rounded-sm shadow-sm flex-shrink-0" />
      <span className="text-sm text-gray-700 flex-1 truncate">{country.name}</span>
      {displayMode === 'dialCode' && (
        <span className="text-sm text-gray-400">+{country.dialCode}</span>
      )}
      {isSelected && <CheckIcon className="w-4 h-4 text-mk-pink-600 flex-shrink-0" />}
    </button>
  );
}

// Flag component that renders SVG flags
interface FlagIconProps {
  countryCode: string;
  className?: string;
}

function FlagIcon({ countryCode, className = '' }: FlagIconProps) {
  const FlagComponent = (Flags as Record<string, React.ComponentType<{ className?: string }>>)[
    countryCode.toUpperCase()
  ];

  if (!FlagComponent) {
    // Fallback for unknown country codes
    return (
      <div className={`bg-gray-200 flex items-center justify-center ${className}`}>
        <span className="text-[8px] text-gray-500">{countryCode}</span>
      </div>
    );
  }

  return <FlagComponent className={className} />;
}

// Icons
function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

export { FlagIcon };
